import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getConfig } from '@edx/frontend-platform';
import { useIntl } from '@edx/frontend-platform/i18n';
import { getAuthenticatedHttpClient } from '@edx/frontend-platform/auth';

import { SpinLoader } from '../../../components';
import { useHeaderFooter } from '../../../hooks';
import { ControllerFieldInput, FooterLinks } from '../components';
import { FORGOT_PASSWORD_SUCCESS } from '../Login/constants';
import ForgotPasswordNotification from './ForgotPasswordNotification';
import { validateFormFields } from './utils';
import {
  TOO_MANY_ATTEMPTS,
  INTERNAL_SERVER_ERROR,
  FORGOT_PASSWORD_INITIAL_FORM_STATE,
  FORGOT_PASSWORD_INITIAL_CODE_STATE,
} from './constants';
import type { FieldTypes, FieldErrorsTypes, ErrorsTypes } from './types';

import '../Logistration.scss';
import messages from './messages';

export interface ForgotPasswordProps {
  children: React.ReactNode;
}

const ForgotPassword = ({ children }: ForgotPasswordProps) => {
  const [formFields, setFormFields] = useState<FieldTypes>(FORGOT_PASSWORD_INITIAL_FORM_STATE);
  const [errors, setErrors] = useState<FieldErrorsTypes>({});
  const [notificationCode, setNotificationCode] = useState<ErrorsTypes>(FORGOT_PASSWORD_INITIAL_CODE_STATE);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const intl = useIntl();
  const navigate = useNavigate();
  const { headerContent } = useHeaderFooter(children);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormFields(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    const { name } = event.target;
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    const email = formFields.email.trim();
    const validationErrors = validateFormFields({ email }, intl);
    if (validationErrors.email) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const client = getAuthenticatedHttpClient();
      await client.post(
        `${getConfig().LMS_BASE_URL}/account/password`,
        { email },
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, isPublic: true },
      );

      navigate('/login', {
        state: { code: FORGOT_PASSWORD_SUCCESS },
        replace: true,
      });
    } catch (error: any) {
      const status = error?.response?.status;
      setNotificationCode(prev => ({
        type: status === 403 ? TOO_MANY_ATTEMPTS : INTERNAL_SERVER_ERROR,
        count: prev.count + 1,
        context: {},
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container-center-horizontal">
      <div className="logistration-main screen">
        {headerContent}
        <div className="logistration">
          <div className="auth-page-block">
            <div className="auth-page-main-content">
              <ForgotPasswordNotification
                code={notificationCode.type}
                count={notificationCode.count}
                context={notificationCode.context}
              />
              <h2 className="auth-page-title">{intl.formatMessage(messages.title)}</h2>
              <div className="auth-title-desc">{intl.formatMessage(messages.instructions)}</div>
              <form className="auth-form">
                <ControllerFieldInput
                  name="email"
                  placeholder={intl.formatMessage(messages.fieldEmail)}
                  value={formFields.email}
                  handleChange={handleChange}
                  handleFocus={handleFocus}
                  error={errors.email}
                />
                <button
                  className="auth-submit-btn"
                  type="submit"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <SpinLoader size={20} color="#fff" /> : intl.formatMessage(messages.continue)}
                </button>
              </form>
              <div className="auth-redirect-block">
                <span>{intl.formatMessage(messages.haveAccount)} </span>
                <Link className="auth-link-redirect" to="/login">
                  {intl.formatMessage(messages.logIn)}
                </Link>
              </div>
            </div>
            <FooterLinks />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
